import React, { RefObject, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CellRendererProps, FlatList, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
    Extrapolation,
    clamp,
    interpolate,
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useRoute } from '@react-navigation/native';

import { EventInfo } from '@/api/events/types/types';
import { Box, Row } from '@/components/lib/components';
import { common } from '@/styles/styles';
import { IndividualVersusEvent, League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { isPGA } from '@/utils/league';
import { BottomSheetFlatList, BottomSheetFlatListMethods, BottomSheetView } from '@gorhom/bottom-sheet';

import { MODAL_HANDLE_HEIGHT } from '../alerts/components/Modal';
import { PlayerWithTeam, PlayerWithTeamAndEvent } from '../betslip-pickem/types';
import { useFantasyFullScreenPagerTransition } from '../circular-pager/FantasyFullScreenPagerTransitionProvider';
import { useScrollableModalContext } from '../circular-pager/ScrollableModalContextProvider';
import { EVENT_HEADER_HERO_IMAGE_HEIGHT, EventHeader } from '../lobby/components/EventHeader';
import { LobbyLeagueComponents, LobbyLeagueSection } from '../lobby/components/LobbyList';
import { PICK_BAR_HEIGHT } from '../lobby/components/Pickbar';
import { StickyCellRendererComponent } from '../lobby/components/sticky-item/StickyCellRendererComponent';
import { useFantasyEventContent } from '../lobby/hooks/use-fantasy-event-content';
import { usePlayerFilters } from '../lobby/hooks/use-player-filters';
import { StickyWrapper } from '../lobby/hooks/use-sticky-translation';
import { Filters } from '../lobby/utils/filters';
import { MaxWidthWrapper } from '../responsive-design/WebComponents';
import { FantasyEventsSheetsParamsList } from './FantasyEventsSheets';
import { EventDetailsSheetHandle } from './components/EventDetailsSheetHandle';
import { EVENT_SHEET_TITLE_HEIGHT } from './components/EventSheetTitle';
import { PickemEventHud } from './components/PickemEventHud';
import { CardSegmentTitle, CardSegmentTitleType } from './components/ufc-events/CardSegmentTitle';
import { UfcFightRow } from './components/ufc-events/UfcFightRow';

type EventBottomSheetPlayersListProps = {
    eventInfo: EventInfo;
    isActive: boolean;
    eventDedicatedHeaderInfo: EventInfo | null;
};

const ListComponent = isWeb ? FlatList : BottomSheetFlatList;

const stickyHeaderHeight = EVENT_HEADER_HERO_IMAGE_HEIGHT - MODAL_HANDLE_HEIGHT - EVENT_SHEET_TITLE_HEIGHT;

/**
 * The event details list, rendering the event header, player filters and the players list for a selected event.
 */
export const EventDetailsScreen = memo(
    ({ isActive, eventInfo, eventDedicatedHeaderInfo }: EventBottomSheetPlayersListProps) => {
        const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();
        const { listRef, filtersStickPosition, listScrollOffset } = usePlayerFilters();
        const eventDedicatedHeader = isPGA(params.league) ? eventInfo : eventDedicatedHeaderInfo;

        const [shouldRenderPlayers, setShouldRenderPlayers] = useState(false);
        const { sheetMountAnimationProgress } = useFantasyFullScreenPagerTransition();

        useAnimatedReaction(
            () => sheetMountAnimationProgress.value,
            current => {
                if (current !== null) {
                    //we don't show the players list when the screen transition/mount starts, for better performance
                    //we only start showing the projection list after 70% of the mount animation is completed
                    if (sheetMountAnimationProgress.value > -0.7 && !shouldRenderPlayers) {
                        runOnJS(setShouldRenderPlayers)(true);
                    }
                }
            }
        );

        const { sortedPlayers, renderPlayerItem, renderFilters, renderLeagueFooter, filterType, sortedUfcEvents } =
            useFantasyEventContent({
                league: params.league,
                eventId: eventInfo.id,
                isActive,
            });

        const shouldShowFights = params.league === League.Ufc && filterType === Filters.Position;

        const { scrollOffset, sheetScrollableHook } = useScrollableModalContext();

        useAnimatedReaction(
            () => scrollOffset.value,
            current => {
                listScrollOffset.value = current;
            }
        );

        const renderItem = useCallback(
            ({
                item,
                index: itemIndex,
            }: {
                item: PlayerWithTeamAndEvent | LobbyLeagueSection | IndividualVersusEvent | CardSegmentTitleType;
                index: number;
            }) => {
                if (typeof item === 'string') {
                    if (item === 'filters') {
                        return (
                            <StickyWrapper initialPosition={filtersStickPosition} scrollOffset={scrollOffset}>
                                <Box backgroundColor={'gray8'}>{renderFilters()}</Box>
                            </StickyWrapper>
                        );
                    }
                    return null;
                }

                if (isCardSegmentTitle(item)) {
                    return (
                        <CardSegmentTitle
                            cardSegmentDetails={item}
                            // index 0 is used for the Projection/Fights tab filters, so first segment title will be at index 1
                            style={itemIndex === 1 ? styles.firstCardSegmentTitle : undefined}
                        />
                    );
                }

                if (isEvent(item)) {
                    return <UfcFightRow eventInfo={item} />;
                }
                return (
                    <Box backgroundColor={isWeb ? 'gray8' : undefined}>
                        {renderPlayerItem({
                            item,
                            index: itemIndex,
                            ignoreIndexes: LobbyLeagueComponents.ExpandedEvent.length,
                        })}
                    </Box>
                );
            },
            [filtersStickPosition, renderFilters, renderPlayerItem, scrollOffset]
        );

        // need to memoize the header component to prevent re-renders - flickers on event hud
        const HeaderComponent = useMemo(() => {
            return (
                <MaxWidthWrapper>
                    <PickemEventHud event={eventInfo} />
                </MaxWidthWrapper>
            );
        }, [eventInfo]);

        const renderFooterComponent = useCallback(() => {
            //Do not render the league footer (loading or "No projections available")
            //if the page is not active, to prevent UI jumps
            if (isActive) {
                return renderLeagueFooter();
            } else {
                return null;
            }
        }, [isActive, renderLeagueFooter]);

        const previousIsActive = useRef(isActive);

        const listToRender = useMemo(() => {
            if (!shouldRenderPlayers) {
                return [...LobbyLeagueComponents.ExpandedEvent];
            }
            if (shouldShowFights) {
                return [...LobbyLeagueComponents.ExpandedEvent, ...sortedUfcEvents];
            }
            return [...LobbyLeagueComponents.ExpandedEvent, ...sortedPlayers];
        }, [shouldRenderPlayers, shouldShowFights, sortedPlayers, sortedUfcEvents]);
        const { height } = useWindowDimensions();
        const insets = useSafeAreaInsets();
        const containerStyle = useMemo(() => {
            return {
                // web doesn't have top and bottom insets, so we use a fixed padding so the last item is not cut off
                paddingBottom: (isWeb ? 140 : insets.bottom + insets.top) + 12 + PICK_BAR_HEIGHT,
                paddingTop: eventDedicatedHeader
                    ? EVENT_HEADER_HERO_IMAGE_HEIGHT
                    : EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT,
                minHeight: height + (eventDedicatedHeader ? stickyHeaderHeight : 0),
            };
        }, [insets.bottom, insets.top, eventDedicatedHeader, height]);

        useEffect(() => {
            if (!isActive && previousIsActive.current) {
                listRef.current?.scrollToOffset({ offset: 0, animated: false });
            }
            previousIsActive.current = isActive;
        }, [eventInfo.name, isActive, listRef]);

        const smoothScrollHeaderStyleBelowTabsPullToRefresh = useAnimatedStyle(() => {
            return {
                transform: [
                    {
                        translateY: clamp(
                            interpolate(
                                scrollOffset.value,
                                [-stickyHeaderHeight, stickyHeaderHeight],
                                [stickyHeaderHeight, -stickyHeaderHeight],
                                Extrapolation.CLAMP
                            ),
                            -Infinity,
                            0
                        ),
                    },
                ],
            };
        });

        const eventHeaderStyle = useAnimatedStyle(() => {
            return {
                opacity: interpolate(
                    scrollOffset.value,
                    [0, (stickyHeaderHeight * 3) / 4],
                    [0, 1],
                    Extrapolation.CLAMP
                ),
            };
        });

        // Using a custom cell renderer component to make sure the filters are rendered above the other items
        // because we stick the filters to the top of the screen
        // and we need to make sure the other items are rendered below it
        const CellRendererComponent = useCallback(
            ({
                children,
                style,
                ...props
            }: CellRendererProps<
                PlayerWithTeam | LobbyLeagueSection | IndividualVersusEvent | CardSegmentTitleType
            >) => {
                const renderingFilters = props.cellKey === 'filters';
                const adjustedStyle = {
                    style,
                    zIndex: renderingFilters ? 100 : undefined,
                    top: 0,
                };
                // StickyCellRendererComponent doesn't work properly on web, only part of the player list is rendering
                // Best solution for now is to remove the sticky tabs on web (players/projections)
                return isWeb ? (
                    <Box style={style} {...props}>
                        {children}
                    </Box>
                ) : (
                    <StickyCellRendererComponent
                        {...props}
                        shouldBeSticky={renderingFilters}
                        style={adjustedStyle}
                        onLayout={event => {
                            if (renderingFilters) {
                                // Projection / Position filters should stick below the event sheet title
                                filtersStickPosition.value =
                                    event.nativeEvent.layout.y - MODAL_HANDLE_HEIGHT - EVENT_SHEET_TITLE_HEIGHT;
                            }
                            props.onLayout?.(event);
                        }}
                    >
                        {children}
                    </StickyCellRendererComponent>
                );
            },
            [filtersStickPosition]
        );

        return (
            <Box flex={1} minHeight={'100%'} backgroundColor={'gray8'}>
                <EventDetailsSheetHandle eventInfo={eventInfo} eventDedicatedHeaderInfo={eventDedicatedHeader} />
                <MaxWidthWrapper>
                    <Box borderTopLeftRadius={'r16'} borderTopRightRadius={'r16'} zIndex={-1}>
                        {eventDedicatedHeader ? (
                            <Box
                                borderTopLeftRadius={'r16'}
                                style={common.borderCurve}
                                borderTopRightRadius={'r16'}
                                position={'absolute'}
                                width={'100%'}
                                zIndex={-1}
                                overflow={'hidden'}
                            >
                                <Animated.View style={smoothScrollHeaderStyleBelowTabsPullToRefresh}>
                                    <EventHeader
                                        singleEvent={eventDedicatedHeader}
                                        headerOverlayStyle={eventHeaderStyle}
                                    />
                                </Animated.View>
                            </Box>
                        ) : null}
                    </Box>
                </MaxWidthWrapper>
                <Box flex={1} minHeight={'100%'}>
                    <ListComponent
                        ListHeaderComponent={HeaderComponent}
                        ListEmptyComponent={ListEmptyComponent}
                        ListFooterComponent={renderFooterComponent}
                        data={listToRender}
                        windowSize={3}
                        maxToRenderPerBatch={10}
                        keyExtractor={keyExtractor}
                        renderItem={renderItem}
                        CellRendererComponent={CellRendererComponent}
                        contentContainerStyle={containerStyle}
                        ref={listRef as RefObject<BottomSheetFlatListMethods>}
                        showsVerticalScrollIndicator={false}
                        scrollEventsHandlersHook={sheetScrollableHook}
                        // needed for sticky components to work on Android
                        removeClippedSubviews={false}
                    />
                </Box>
            </Box>
        );
    }
);

// mock empty component for loading state
export const ListEmptyComponent = () => {
    const { height } = useWindowDimensions();
    return (
        <BottomSheetView>
            <Row justifyContent={'center'} paddingVertical={'s20'} height={height}>
                <Box />
            </Row>
        </BottomSheetView>
    );
};

const keyExtractor = (
    item: PlayerWithTeam | LobbyLeagueSection | IndividualVersusEvent | CardSegmentTitleType,
    index: number
) => {
    if (typeof item === 'string') {
        return `${item}`;
    }
    if (item.__typename === 'CardSegmentTitle') {
        return `card-segment-${item.cardSegment}`;
    } else {
        return `player-${item.id}-${index}`;
    }
};

const isEvent = (item: EventInfo | PlayerWithTeam): item is EventInfo => item.__typename !== 'Player';

const isCardSegmentTitle = (cardSegmentDetails: {
    __typename?: string;
}): cardSegmentDetails is CardSegmentTitleType => {
    return cardSegmentDetails.__typename === 'CardSegmentTitle';
};

const styles = StyleSheet.create({
    firstCardSegmentTitle: {
        paddingTop: 0,
    },
});
